import React, {useEffect, useState} from "react";
import {Settings} from "../../../models/models";
import {PAYMENT_TYPE, SUBSCRIPTION_ROOT_PAGES, VRI_LEVEL, VRI_PAGES} from "../../../constants";
import Loading from "../../common/Loading";
import {Elements} from "@stripe/react-stripe-js";
import {loadStripe} from "@stripe/stripe-js";
import {
    cancelPaymentIntent,
    cancelSetupIntent,
    createCustomerForAccount,
    createPaymentIntent, createSetupIntent, setVRIDoNotRenew, updateVRISubscription
} from "../../../api/requests";
import useBeforeUnload from "../../../helpers/useBeforeUnload";
import {
    daysBetween, getPreferredVRIPaymentMethod,
    getSameVRITierPlanAtDifferentRecurrence,
    setSelectedPlanAsOriginal
} from "../../../helpers/helpers";
import PlanHeaderNotes from "../../common/subscriptions/PlanHeaderNotes";
import ManagePage from "../common/ManagePage";
import UpdateSubscriptionPage from "../common/UpdateSubscriptionPage";
import CheckoutPage from "../common/CheckoutPage";
import UpdateSubscriptionConfirmPage from "../common/UpdateSubscriptionConfirmPage";
import SplashPage from "../common/SplashPage";
import PlanSelectPage from "../common/PlanSelectPage";

const VriContainer = (props: {
    setData: Function, settings: Settings,
    handleError: Function, setServerResponse: Function, rootPage: number, setRootPage: Function,
    setShouldRefresh: Function, preferredVRIPaymentMethod: Object
}) => {
    const {
        appData,
        data,
        setData,
        settings,
        handleError,
        setServerResponse,
        rootPage,
        setRootPage,
        setShouldRefresh,
        preferredVRIPaymentMethod
    } = props;
    if (!data || !settings || rootPage !== SUBSCRIPTION_ROOT_PAGES.VRI) {
        return <></>;
    }

    const {
        level, levels, expirationDate,
        customerId, isEligibleForFreeTrial
    } = data;

    const {publishableKey} = settings;

    const {allPaymentMethods} = appData;

    const isNone = level === VRI_LEVEL.NONE;
    const daysUntilExpired = daysBetween(new Date(), new Date(expirationDate));
    const hasExpired = daysUntilExpired < 0;

    const [page, setPage] = useState(VRI_PAGES.SPLASH);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [clientSecretKey, setClientSecretKey] = useState(""); // Unique checkout session key for customer.
    const [payNow, setPayNow] = useState(null);

    // A PaymentIntent is used to keep track of the payment flow and is created upon
    // React app load: https://stripe.com/docs/payments/payment-intents
    const [paymentIntentId, setPaymentIntentId] = useState("");

    useEffect(() => {
        if (page === VRI_PAGES.PLAN_SELECT && !customerId) {
            createCustomer();
        } else if (page === VRI_PAGES.CHECKOUT) {
            if (!publishableKey || !selectedPlan) {
                handleError(i18n.payments.error.genericTitle, i18n.payments.error.failedToLoadCheckoutForm, setSelectedPlan);
                return;
            } else if (!customerId) {
                createCustomer(() => {
                    createIntent();
                });
                return;
            }

            createIntent();
        }
    }, [page]);

    const createCustomer = (callback) => {
        setLoading(true);
        createCustomerForAccount((result) => {
            setLoading(false);
            if (result.status !== 200) {
                console.error('Failed to create customer for account');
                handleError(result, i18n.payments.error.genericServerError, setServerResponse);
                return;
            }

            setData({
                ...data,
                customerId: result.data.customerId,
            });

            if (callback) {
                callback();
            }
        });
    };

    const createIntent = () => {
        setLoading(true);
        setPaymentIntentId("");

        if (payNow) {
            createPaymentIntent(selectedPlan.productId, PAYMENT_TYPE.VRI, (result) => {
                setLoading(false);
                if (result.status !== 200) {
                    console.error('Failed to create payment intent');
                    handleError(result, i18n.payments.error.genericServerError, setServerResponse);
                    return;
                }
                setPaymentIntentId(result.data.id);
                setClientSecretKey(result.data.clientSecret);
            });
        } else {
            createSetupIntent(selectedPlan.productId, PAYMENT_TYPE.VRI, (result) => {
                setLoading(false);
                if (result.status !== 200) {
                    console.error('Failed to create setup intent');
                    handleError(result, i18n.payments.error.genericServerError, setServerResponse);
                    return;
                }
                setPaymentIntentId(result.data.id);
                setClientSecretKey(result.data.clientSecret);
            });
        }
    };

    useEffect(() => {
        if (payNow === null) {
            return;
        }

        if (!payNow && level !== undefined) {
            setSelectedPlanAsOriginal(levels, level, setSelectedPlan);
        }
    }, [payNow]);

    // Clean Up //////////////////////////////////////////////////////////////
    const handlePreventDefault = (event) => {
        event.preventDefault(); // Cancel the event as stated by the standard.
        event.returnValue = ''; // Chrome requires returnValue to be set.
    }

    const handleLeavingPage = (event) => {
        // Cancel any payment attempts that have not yet been processed.
        if (page === VRI_PAGES.CHECKOUT && paymentIntentId) {
            handlePreventDefault(event);

            if (paymentIntentId.includes('seti')) {
                cancelSetupIntent(paymentIntentId, (result) => {
                });
            } else {
                cancelPaymentIntent(paymentIntentId, (result) => {
                });
            }
        }
    };

    // Custom hook to handle applying the 'beforeunload' event listener smoothly.
    useBeforeUnload(handleLeavingPage);

    // UI ///////////////////////////////////////////////////////////////////////////
    let Body = <></>;
    if (loading) {
        Body = (<Loading show={true}/>);
    } else if (page === VRI_PAGES.SPLASH) {
        Body = (
            <SplashPage
                data={data}
                setPage={setPage}
                setPayNow={setPayNow}
                setRootPage={setRootPage}
                pages={VRI_PAGES}
                paymentType={PAYMENT_TYPE.VRI}
                levelType={VRI_LEVEL}
            />
        )
    } else if (page === VRI_PAGES.PLAN_SELECT) {
        Body = (
            <PlanSelectPage
                data={data}
                setData={setData}
                settings={settings}
                payNow={payNow}
                setPage={setPage}
                selectedPlan={selectedPlan}
                setSelectedPlan={setSelectedPlan}
                isEligibleForFreeTrial={isEligibleForFreeTrial}
                pages={VRI_PAGES}
                paymentType={PAYMENT_TYPE.VRI}
            />
        );
    } else if (page === VRI_PAGES.CHECKOUT && clientSecretKey) {
        Body = (
            <Elements stripe={loadStripe(publishableKey)}
                      options={{clientSecret: clientSecretKey}}>
                <CheckoutPage
                    data={data}
                    selectedPlan={selectedPlan}
                    payNow={payNow}
                    isEligibleForFreeTrial={isEligibleForFreeTrial}
                    paymentIntentId={paymentIntentId}
                    handleError={handleError}
                    setServerResponse={setServerResponse}
                    setPage={setPage}
                    preferredPaymentMethodOrNull={getPreferredVRIPaymentMethod(allPaymentMethods)?.paymentMethodId || null}
                    paymentType={PAYMENT_TYPE.VRI}
                    pages={VRI_PAGES}/>
            </Elements>
        );
    } else if (page === VRI_PAGES.MANAGE) {
        Body = (
            <ManagePage
                data={data}
                pages={VRI_PAGES}
                setPage={setPage}
                setPayNow={setPayNow}
                setLoading={setLoading}
                setServerResponse={setServerResponse}
                setShouldRefresh={setShouldRefresh}
                preferredPaymentMethod={preferredVRIPaymentMethod}
                paymentType={PAYMENT_TYPE.VRI}
                hasSubscription={true}
                setDoNotRenew={setVRIDoNotRenew}/>
        );
    } else if (page === VRI_PAGES.UPDATE_SUBSCRIPTION) {
        Body = (
            <UpdateSubscriptionPage
                pages={VRI_PAGES}
                data={data}
                setData={setData}
                payNow={payNow}
                setPage={setPage}
                selectedPlan={selectedPlan}
                setSelectedPlan={setSelectedPlan}
                getTierPlanAtDifferentRecurrence={getSameVRITierPlanAtDifferentRecurrence}
                paymentType={PAYMENT_TYPE.VRI}/>
        );
    } else if (page === VRI_PAGES.UPDATE_SUBSCRIPTION_CONFIRM) {
        Body = (
            <UpdateSubscriptionConfirmPage
                data={data}
                selectedPlan={selectedPlan}
                handleError={handleError}
                setServerResponse={setServerResponse}
                setPage={setPage}
                setSelectedPlan={setSelectedPlan}
                getSameTierPlanAtDifferentRecurrence={getSameVRITierPlanAtDifferentRecurrence}
                pages={VRI_PAGES}
                updateSubscription={updateVRISubscription}
            />
        );
    }

    return (
        <div className={`plan-container ${hasExpired && 'plan-container-expired'}`}>
            <PlanHeaderNotes title={i18n.payments.vri.title}
                             subtitle={i18n.payments.vri.productNote}
                             paymentType={PAYMENT_TYPE.VRI}
                             data={data}/>
            {Body}
        </div>
    );
}

export default VriContainer;